"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, Shield, ShieldAlert, ShieldCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const solutions = [{
  id: "safety-security",
  title: "Safety & Security",
  badge: "Hazardous Materials",
  description: "Rapid identification of unknown chemicals, narcotics, and toxic industrial compounds to protect personnel and facilities.",
  link: "/SafetyAndSecurityPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/safety_security_inspection.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/safety_security_inspection.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/safety_security_inspection.png-wvc-sizes",
  imageAlt: "Field safety and security testing equipment in use",
  icon: ShieldCheck,
  highlights: ["Unknown chemical identification", "Handheld Raman and NIR analyzers", "Hazardous material threat screening"]
}, {
  id: "border-security",
  title: "Border Security & Defence",
  badge: "Field Deployable",
  description: "Ruggedized analytical tools for rapid customs checkpoints, port inspections, and military intelligence operations.",
  link: "/BorderSecurityAndDefencePage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/border_defence_screening.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/border_defence_screening.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/border_defence_screening.png-wvc-sizes",
  imageAlt: "Border security inspection station and detection instruments",
  icon: Shield,
  highlights: ["Customs and port of entry screening", "Explosives and precursor detection", "Through-barrier container scanning"]
}, {
  id: "law-enforcement",
  title: "Law Enforcement & Public Safety",
  badge: "Forensics & Police",
  description: "Definitive evidence collection and trace chemical analysis for first responders, forensic units, and public safety officers.",
  link: "/LawEnforcementPublicSafetyPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/law_enforcement_forensics.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/law_enforcement_forensics.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/law_enforcement_forensics.png-wvc-sizes",
  imageAlt: "Forensic and law enforcement laboratory analysis tools",
  icon: ShieldAlert,
  highlights: ["Illicit substance verification", "Non-destructive evidence handling", "Real-time tactical library updates"]
}];
export default function SecuritySolutions() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="securitysolutionsgrid" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="page-container">
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4">
            Security & Defence Solutions
          </Badge>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-foreground font-default">
            Specialized Analytical Technology for Critical Operations
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            Reliable field-ready and laboratory instruments engineered to deliver rapid, definitive material identification across regulated environments.
          </p>
        </div>

        {/* 3-Column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {solutions.map((solution, i) => {
          const IconComponent = solution.icon;
          return <motion.div key={solution.id} data-index={i} initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-50px"
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }} className="flex">
                <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-200 overflow-hidden">
                  <div className="flex flex-col">
                    {/* Visual Media */}
                    <div className="relative aspect-16/10 w-full overflow-hidden bg-muted">
                      <img src={solution.image} data-wvc-srcset={solution.imageSrcset} data-wvc-sizes={solution.imageSizes} alt={solution.imageAlt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 hover:scale-105" />
                      <div className="absolute top-3 left-3">
                        <Badge variant="secondary" className="bg-background/90 backdrop-blur-xs text-foreground">
                          {solution.badge}
                        </Badge>
                      </div>
                    </div>

                    <CardHeader className="pt-6 pb-3">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 rounded-md bg-secondary text-secondary-foreground">
                          <IconComponent className="size-5 text-primary" />
                        </div>
                        <CardTitle className="text-xl font-semibold font-default">
                          {solution.title}
                        </CardTitle>
                      </div>
                      <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                        {solution.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="py-2">
                      <div className="border-t border-border/60 pt-4">
                        <p className="text-xs font-semibold text-foreground tracking-wide uppercase mb-3">
                          Key Capabilities
                        </p>
                        <ul className="space-y-2 text-sm text-muted-foreground">
                          {solution.highlights.map((highlight, hIndex) => <li key={hIndex} data-index={hIndex} className="flex items-start gap-2">
                              <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                              <span>{highlight}</span>
                            </li>)}
                        </ul>
                      </div>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-4 pb-6">
                    <Button asChild variant="outline" className="w-full justify-between group">
                      <Link to={solution.link}>
                        <span>Explore Solution</span>
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}